import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { format } from 'date-fns';
import type { ReactElement } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { useRecordPriceMutation } from '@/api/mifid';
import { useTargetAccumulatorSelector } from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { getTargetAccumulatorQuote, getTargetAccumulatorRfsId } from '@/store/targetAccumulator/selectors.ts';
import { FULL_DATE_FORMAT_TEXT } from '@/utils/date.ts';

export function TimestampButton({ tileId }: { tileId: string }): ReactElement {
  const [recordPrice] = useRecordPriceMutation();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { mifid, rfsStatus, quoteId, rfsId, quoteCcy } = useTargetAccumulatorSelector((targetAccumulator) => {
    const quote = getTargetAccumulatorQuote(targetAccumulator);
    const currencyIndex = targetAccumulator.properties.premiumTypeCurrencyIndex;

    return {
      rfsId: getTargetAccumulatorRfsId(targetAccumulator),
      quoteId: quote?.quoteId,
      quoteCcy: currencyIndex === 1 ? quote?.premiumData.premiumCcy1 : quote?.premiumData.premiumCcy2,
      mifid: targetAccumulator.properties.mifid,
      rfsStatus: targetAccumulator.properties.rfs?.status,
    };
  });

  const hasBeenUsed = isDefined(mifid?.records?.length) ? mifid.records.length > 0 : false;
  const isDisabled = rfsStatus !== 'PRICING';

  function handleRecordPrice() {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      'Timestamp button clicked',
      isDefined(rfsId) ? rfsId : 'unknown',
    );
    if (isNotDefined(rfsId) || isNotDefined(quoteCcy) || isNotDefined(quoteId)) {
      return;
    }

    recordPrice({
      tileId,
      date: Date.now(),
      quoteId,
      rfsId,
      prices: [
        {
          bid: 0,
          ask: Number.isNaN(Number(quoteCcy)) ? 0 : Number(quoteCcy),
        },
      ],
    });
  }

  return (
    <div className="d-flex">
      <Tooltip contents={<FormattedMessage id="accumulator.footer.timestampBtn.tooltip" />}>
        <Button
          size="sm"
          purpose="primary"
          color={isDisabled || hasBeenUsed ? 'primary' : 'danger'}
          iconPosition="start"
          disabled={isDisabled}
          onClick={handleRecordPrice}
          data-e2e={'button-timestamp'}
        >
          <i className="icon icon-sm">timer</i>
          <FormattedMessage id="accumulator.footer.timestampBtn" />
        </Button>
      </Tooltip>
      {!isDisabled && <PriceRecordHistory />}
    </div>
  );
}

function PriceRecordHistory() {
  const { currencyPair, mifid } = useTargetAccumulatorSelector((targetAccumulator) => ({
    currencyPair: targetAccumulator.properties.currencyPair,
    mifid: targetAccumulator.properties.mifid || { records: [] },
  }));

  return (
    <OverlayTrigger
      rootClose
      placement="bottom"
      trigger="click"
      overlay={
        <Popover style={{ maxWidth: '300px' }}>
          <Popover.Header data-e2e="timestamp-popover-title">
            <div className="d-flex flex-column">
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.title"
                values={{ currencyPair, instrument: 'Accumulator' }}
              />
              <FormattedMessage
                tagName="div"
                id="priceRecordsHistory.popover.subtitle"
                values={{
                  date: isDefined(mifid.rfsDate) ? format(mifid.rfsDate, FULL_DATE_FORMAT_TEXT) : '',
                }}
              />
            </div>
          </Popover.Header>

          <Popover.Body>
            <div className="d-flex flex-column gap-1" data-e2e="timestamp-popover-body">
              {mifid.records.toReversed().map((record, index) => (
                <div
                  // oxlint-disable-next-line no-array-index-key -- index is safe here because the list is static and not re-orderable
                  key={`${record.date}-${index}`}
                >
                  {record.isError ? (
                    <div className="text-danger">
                      <i className="icon icon-sm">error</i>
                      <FormattedMessage
                        id="priceRecordsHistory.popover.item.error"
                        values={{
                          item: mifid.records.length - index,
                          date: format(record.date, FULL_DATE_FORMAT_TEXT),
                        }}
                      />
                    </div>
                  ) : (
                    <FormattedMessage
                      id="priceRecordsHistory.popover.item"
                      values={{
                        item: mifid.records.length - index,
                        date: format(record.date, FULL_DATE_FORMAT_TEXT),
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </Popover.Body>
        </Popover>
      }
    >
      {mifid.records.some((r) => r.isError) ? (
        <div className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer text-danger">
          <i className="icon icon-sm">error</i>
          <FormattedMessage id="priceRecordsHistory.recording.error" />
        </div>
      ) : (
        <div
          data-e2e="timestamp-count"
          className="border badge rounded-pill badge-discreet-secondary fs-12 cursor-pointer"
        >
          {mifid.records?.length ?? 0}
        </div>
      )}
    </OverlayTrigger>
  );
}
